// Import styles
import './globals/global.scss';

// React imports
import React from 'react';
import { createRoot } from 'react-dom/client';

// MSAL
import { PublicClientApplication, EventType, AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalConfig, loginRequest } from './configs/authConfig';

// App and utilities
import App from './App';
import './i18n';

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Render the app
function renderApp() {
  const rootElement = document.getElementById('app');
  if (!rootElement) {
    throw new Error('Failed to find the root element');
  }

  const root = createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <App msalInstance={msalInstance} />
    </React.StrictMode>
  );
}

// Initialize MSAL and handle authentication before rendering
async function initializeApp() {
  await msalInstance.initialize();

  // Listen for login events to set active account
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      msalInstance.setActiveAccount(payload.account);
    }
  });

  // Handle the redirect response if returning from login
  const response = await msalInstance.handleRedirectPromise();

  if (response) {
    // Returning from login redirect - set active account and render
    msalInstance.setActiveAccount(response.account);
    renderApp();
    return;
  }

  // Check if there's already an active account
  const accounts = msalInstance.getAllAccounts();

  if (accounts.length > 0) {
    // User has accounts - set active and render app
    msalInstance.setActiveAccount(accounts[0]);
    renderApp();
    return;
  }

  // No accounts - need to authenticate
  // Trigger redirect login directly (skip silent auth which causes iframe issues)
  await msalInstance.loginRedirect(loginRequest);
}

// Start the app
initializeApp().catch((error) => {
  console.error('Authentication error:', error);
  // If there's an auth error, still try to render the app
  // MsalAuthenticationTemplate will handle showing the error
  renderApp();
});